import React, { useEffect, useState } from "react";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from "V2.0/common/dropdown";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "V2.0/common/table";
import { EllipsisHorizontalIcon } from "@heroicons/react/16/solid";
import * as knowledgeService from "V2.0/services/knowledgebaseService";
import {
  Alert,
  AlertActions,
  AlertDescription,
  AlertTitle,
} from "V2.0/common/alert";
import { Button } from "V2.0/common/button";
import { message } from "antd";
import Empty from "V2.0/images/empty.svg";
import { PlusIcon } from "@heroicons/react/24/outline";
import { Dialog, DialogTitle, DialogBody } from "V2.0/common/dialog";
import AddURLData from "./URL/Add_url";
import { DialogTitleWithClose } from "../HomeModule/CustomAPIs/components/Dialog/DialogTitleWithClose";
import { deleteCorpus, deleteCorpusByEtag } from "./service";
import WebLinkAdd from "./WebLinks/WebLinkAdd";
import { showErrorToast } from "V2.0/utils";
import { Link } from "react-router-dom";
import { Badge } from "V2.0/common/badge";

function AddURL(props) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        outline
      >
        <PlusIcon /> Add Web link
      </Button>
      <Dialog
        size="5xl"
        open={open}
        onClose={() => {}}
      >
        <DialogTitle>
          <DialogTitleWithClose
            title="Add URL"
            onClose={() => setOpen(false)}
          />
        </DialogTitle>
        <DialogBody>
          <div className="">
            <AddURLData
              {...props}
              onClose={() => setOpen(false)}
            />
          </div>
        </DialogBody>
      </Dialog>
    </>
  );
}

function WebLinks({
  KB,
  projects,
  activeProject,
  reload,
  setReload,
  refetch,
  isViewOnly,
}) {
  let [isOpen, setIsOpen] = useState(false);
  const [eTag, setEtag] = useState("");
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    message.loading("Deleting Web link");
    setLoading(true);
    try {
      const response = await deleteCorpus(
        KB._id,
        projects[activeProject]._id,
        eTag,
      );

      if (response.status === 200) {
        message.destroy();
        message.success("Web link deleted");
        refetch(projects[activeProject]._id, KB._id);
        setIsOpen(false);
        setLoading(false);
        setReload(!reload);
      }
    } catch (e) {
      message.destroy();
      showErrorToast(e, "Failed to delete web link");
      setIsOpen(false);
      setLoading(false);
    }
  };

  const showDeleteModal = (etag) => {
    setIsOpen(true);
    setEtag(etag);
  };

  const Skeleton = () => {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 rounded"></div>
      </div>
    );
  };

  const projectId = projects[activeProject]?._id;

  return (
    <div className="flex flex-col max-h-[85dvh] p-8 gap-y-4">
      <div className="flex justify-between items-center pb-4 border-b">
        <p className="text-xl font-semibold">Web Links</p>
        {!isViewOnly && (
          <WebLinkAdd
            workspaceId={projectId}
            kbId={KB._id}
            fetchKb={refetch}
          />
        )}
      </div>
      <div className="border rounded-lg h-full p-4">
        <Table className="[--gutter:theme(spacing.6)] sm:[--gutter:theme(spacing.8)] overflow-scroll max-h-[80dvh]">
          <TableHead>
            <TableRow>
              <TableHeader>Data Type</TableHeader>
              <TableHeader>Link</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Actions</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {KB && KB.corpus ? (
              KB.corpus.filter((corpus) => corpus.type === "url").length > 0 ? (
                KB.corpus
                  .filter((corpus) => corpus.type === "url")
                  .map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{item.type}</TableCell>
                      <TableCell
                        className="truncate"
                        style={{ maxWidth: "200px" }}
                      >
                        <Link
                          to={item.location}
                          target="_blank"
                        >
                          {item.location}
                        </Link>
                      </TableCell>
                      <TableCell className="text-zinc-500">
                        <Badge>{item.state}</Badge>
                      </TableCell>
                      <TableCell>
                        {!isViewOnly && (
                          <div className="-mx-3 -my-1.5 sm:-mx-2.5">
                            <Dropdown>
                              <DropdownButton
                                plain
                                aria-label="More options"
                              >
                                <EllipsisHorizontalIcon />
                              </DropdownButton>
                              <DropdownMenu anchor="bottom end">
                                <DropdownItem
                                  onClick={() => showDeleteModal(item.eTag)}
                                >
                                  Delete
                                </DropdownItem>
                              </DropdownMenu>
                            </Dropdown>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableCell
                  colSpan={4}
                  className="text-center"
                >
                  <div className="flex flex-col items-center justify-center m-5">
                    <img
                      src={Empty}
                      alt="No files"
                      className="w-16 h-16"
                    />
                    <p>You might not have added any Web links yet</p>
                  </div>
                </TableCell>
              )
            ) : (
              [...Array(3)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                  <TableCell>
                    <Skeleton />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <Alert
        open={isOpen}
        onClose={setIsOpen}
      >
        <AlertTitle>Delete Link?</AlertTitle>
        <AlertDescription>
          By deleting this link, your assistant will no longer have access to
          this knowledge.
        </AlertDescription>
        <AlertActions>
          <Button
            plain
            onClick={() => setIsOpen(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            loading={loading}
          >
            Delete
          </Button>
        </AlertActions>
      </Alert>
    </div>
  );
}

export default WebLinks;
